/**
 * CVE Management Modal
 * Allows admins to associate or dissociate CVEs with versions
 */

import { useState } from 'react';
import {
  useLazyFetchCVEFromNVDQuery,
  useAssociateCVEWithVersionMutation,
  useRemoveCVEFromVersionMutation,
} from '../services/api';
import type { SoftwareVersion } from '@patch-manager/shared';

interface CVEManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  version: SoftwareVersion;
  existingCVEs?: Array<{ cve_id: string; status: string }>;
}

export function CVEManageModal({
  isOpen,
  onClose,
  version,
  existingCVEs = [],
}: CVEManageModalProps) {
  const [fetchCVE, { isFetching }] = useLazyFetchCVEFromNVDQuery();
  const [associateCVE, { isLoading: isAssociating }] = useAssociateCVEWithVersionMutation();
  const [removeCVE, { isLoading: isRemoving }] = useRemoveCVEFromVersionMutation();

  const [cveInput, setCveInput] = useState('');
  const [cveStatus, setCveStatus] = useState<'affected' | 'fixed'>('affected');
  const [fetchedCVE, setFetchedCVE] = useState<any>(null);
  const [error, setError] = useState('');

  const handleFetchCVE = async () => {
    setError('');
    setFetchedCVE(null);

    // Validate CVE ID format
    if (!/^CVE-\d{4}-\d+$/i.test(cveInput.trim())) {
      setError('Invalid CVE ID format. Use format: CVE-YYYY-NNNNN');
      return;
    }

    const cveId = cveInput.trim().toUpperCase();

    try {
      const result = await fetchCVE(cveId).unwrap();
      setFetchedCVE(result);
    } catch (err: any) {
      setError(err.data?.error || 'Failed to fetch CVE from NVD');
    }
  };

  const handleAssociateCVE = async () => {
    if (!fetchedCVE) return;

    try {
      await associateCVE({
        versionId: version.id,
        cve_id: fetchedCVE.cve_id,
        status: cveStatus,
      }).unwrap();

      alert(`CVE ${fetchedCVE.cve_id} ${cveStatus === 'affected' ? 'marked as vulnerable' : 'marked as patched'}!`);

      // Reset form
      setCveInput('');
      setFetchedCVE(null);
      setError('');
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to associate CVE');
    }
  };

  const handleRemoveCVE = async (cveId: string) => {
    if (!confirm(`Remove ${cveId} from this version?`)) return;

    try {
      await removeCVE({
        versionId: version.id,
        cve_id: cveId,
      }).unwrap();

      alert(`CVE ${cveId} removed successfully!`);
    } catch (err: any) {
      alert(`Failed to remove CVE: ${err.data?.error || err.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage CVEs for {version.version_number}</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {/* Existing CVEs */}
          {existingCVEs.length > 0 && (
            <div className="section">
              <h3>Associated CVEs</h3>
              <div className="cve-list">
                {existingCVEs.map((cve) => (
                  <div key={cve.cve_id} className="cve-item">
                    <div className="cve-info">
                      <span className="cve-id">{cve.cve_id}</span>
                      <span className={`status-badge status-${cve.status}`}>
                        {cve.status}
                      </span>
                    </div>
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveCVE(cve.cve_id)}
                      disabled={isRemoving}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new CVE */}
          <div className="section">
            <h3>Add CVE</h3>

            <div className="form-group">
              <label>CVE ID</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={cveInput}
                  onChange={(e) => setCveInput(e.target.value)}
                  placeholder="CVE-2024-12345"
                  onKeyPress={(e) => e.key === 'Enter' && handleFetchCVE()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleFetchCVE}
                  disabled={isFetching || !cveInput.trim()}
                >
                  {isFetching ? 'Fetching...' : 'Fetch from NVD'}
                </button>
              </div>
              <small className="form-text">
                Enter a CVE ID to fetch details from the National Vulnerability Database
              </small>
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            {fetchedCVE && (
              <div className="cve-details">
                <h4>{fetchedCVE.cve_id}</h4>

                <div className="detail-row">
                  <span className="label">Severity:</span>
                  <span className={`severity-badge severity-${fetchedCVE.severity?.toLowerCase()}`}>
                    {fetchedCVE.severity || 'UNKNOWN'}
                  </span>
                  {fetchedCVE.cvss_score && (
                    <span className="cvss-score">CVSS: {fetchedCVE.cvss_score}</span>
                  )}
                </div>

                <div className="detail-row">
                  <span className="label">Published:</span>
                  <span>{new Date(fetchedCVE.published_date).toLocaleDateString()}</span>
                </div>

                <div className="detail-row full-width">
                  <span className="label">Description:</span>
                  <p className="description">{fetchedCVE.description}</p>
                </div>

                <div className="form-group">
                  <label>Status in this version:</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="status"
                        value="affected"
                        checked={cveStatus === 'affected'}
                        onChange={() => setCveStatus('affected')}
                      />
                      <span>Affected (Vulnerable)</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="status"
                        value="fixed"
                        checked={cveStatus === 'fixed'}
                        onChange={() => setCveStatus('fixed')}
                      />
                      <span>Fixed (Patched)</span>
                    </label>
                  </div>
                </div>

                <button
                  className="btn btn-success"
                  onClick={handleAssociateCVE}
                  disabled={isAssociating}
                >
                  {isAssociating ? 'Associating...' : `Associate as ${cveStatus}`}
                </button>
              </div>
            )}
          </div>
        </div>

        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-content {
            background-color: white;
            border-radius: 8px;
            max-width: 700px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #e0e0e0;
          }

          .modal-header h2 {
            margin: 0;
            font-size: 1.5rem;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 2rem;
            cursor: pointer;
            color: #666;
          }

          .close-button:hover {
            color: #000;
          }

          .modal-body {
            padding: 20px;
          }

          .section {
            margin-bottom: 32px;
          }

          .section h3 {
            margin: 0 0 16px 0;
            font-size: 1.2rem;
            color: #333;
          }

          .cve-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .cve-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }

          .cve-info {
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .cve-id {
            font-weight: 500;
            color: #333;
          }

          .status-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            text-transform: uppercase;
          }

          .status-affected {
            background-color: #fff3cd;
            color: #856404;
          }

          .status-fixed {
            background-color: #d4edda;
            color: #155724;
          }

          .btn-remove {
            padding: 6px 12px;
            background-color: #dc3545;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          }

          .btn-remove:hover:not(:disabled) {
            background-color: #c82333;
          }

          .btn-remove:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .form-group {
            margin-bottom: 20px;
          }

          label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
          }

          .input-group {
            display: flex;
            gap: 8px;
          }

          .form-control {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            color: #333;
            background-color: #fff;
          }

          .form-control:focus {
            outline: none;
            border-color: #0066cc;
            box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
          }

          .form-text {
            display: block;
            margin-top: 4px;
            font-size: 12px;
            color: #666;
          }

          .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s;
          }

          .btn-primary {
            background-color: #0066cc;
            color: white;
          }

          .btn-primary:hover:not(:disabled) {
            background-color: #0052a3;
          }

          .btn-success {
            background-color: #28a745;
            color: white;
            width: 100%;
            margin-top: 16px;
          }

          .btn-success:hover:not(:disabled) {
            background-color: #218838;
          }

          .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .alert {
            padding: 12px;
            border-radius: 4px;
            margin-bottom: 16px;
          }

          .alert-error {
            background-color: #f8d7da;
            color: #721c24;
            border: 1px solid #f5c6cb;
          }

          .cve-details {
            padding: 16px;
            background-color: #f8f9fa;
            border-radius: 4px;
            margin-top: 16px;
          }

          .cve-details h4 {
            margin: 0 0 16px 0;
            color: #333;
          }

          .detail-row {
            display: flex;
            gap: 8px;
            align-items: center;
            margin-bottom: 12px;
          }

          .detail-row.full-width {
            flex-direction: column;
            align-items: flex-start;
          }

          .detail-row .label {
            font-weight: 500;
            color: #666;
          }

          .severity-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            text-transform: uppercase;
          }

          .severity-critical {
            background-color: #721c24;
            color: white;
          }

          .severity-high {
            background-color: #dc3545;
            color: white;
          }

          .severity-medium {
            background-color: #ffc107;
            color: #000;
          }

          .severity-low {
            background-color: #28a745;
            color: white;
          }

          .cvss-score {
            padding: 4px 8px;
            background-color: #e9ecef;
            border-radius: 4px;
            font-size: 13px;
            font-weight: 500;
          }

          .description {
            margin: 8px 0 0 0;
            color: #555;
            line-height: 1.5;
          }

          .radio-group {
            display: flex;
            gap: 16px;
          }

          .radio-label {
            display: flex;
            align-items: center;
            cursor: pointer;
          }

          .radio-label input[type="radio"] {
            margin-right: 8px;
            width: auto;
          }
        `}</style>
      </div>
    </div>
  );
}
